import emailjs from '@emailjs/browser';
import dayjs from 'dayjs';
import logger from './logger';
import debug from './debug';
import { getStoredUtmParameters } from './utmParameters';

// EmailJS Configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const EMAILJS_TEXT_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEXT_TEMPLATE_ID; // optional plain-text template

// Safe logger wrapper to prevent production errors
const safeLogger = {
  warn: (message, data) => {
    try {
      if (logger && logger.warn) {
        logger.warn(message, data);
      } else {
        console.warn(message, data);
      }
    } catch (error) {
      console.warn(message, data);
    }
  },
  info: (message, data) => {
    try {
      if (logger && logger.info) {
        logger.info(message, data);
      } else {
        console.log(message, data);
      }
    } catch (error) {
      console.log(message, data);
    }
  },
  success: (message, data) => {
    try {
      if (logger && logger.success) {
        logger.success(message, data);
      } else {
        console.log(message, data);
      }
    } catch (error) {
      console.log(message, data);
    }
  },
  error: (message, data) => {
    try {
      if (logger && logger.error) {
        logger.error(message, data);
      } else {
        console.error(message, data);
      }
    } catch (error) {
      console.error(message, data);
    }
  }
};

// Initialize EmailJS if variables are available
if (EMAILJS_SERVICE_ID && (EMAILJS_TEMPLATE_ID || EMAILJS_TEXT_TEMPLATE_ID) && EMAILJS_PUBLIC_KEY) {
  emailjs.init(EMAILJS_PUBLIC_KEY);
} else {
  safeLogger.warn('EmailJS environment variables not fully configured', {
    hasServiceId: !!EMAILJS_SERVICE_ID,
    hasTemplateId: !!EMAILJS_TEMPLATE_ID || !!EMAILJS_TEXT_TEMPLATE_ID,
    hasPublicKey: !!EMAILJS_PUBLIC_KEY
  });
}

const prepareLocationData = (location) => {
  if (!location || !location.neighborhoods) {
    return 'Not specified';
  }
  
  // Handle "All Manhattan" case
  if (location.neighborhoods.includes('all-manhattan')) {
    return 'All Manhattan';
  }
  
  // Convert neighborhood keys to readable names
  const neighborhoodMap = {
    'upper-east-side': 'Upper East Side',
    'upper-west-side': 'Upper West Side',
    'midtown': 'Midtown',
    'chelsea': 'Chelsea',
    'west-village': 'West Village',
    'east-village': 'East Village',
    'lower-east-side': 'Lower East Side',
    'soho': 'SoHo',
    'tribeca': 'TriBeCa',
    'financial-district': 'Financial District',
    'brooklyn': 'Brooklyn',
    'queens': 'Queens',
    'bronx': 'Bronx',
    'staten-island': 'Staten Island'
  };
  
  return location.neighborhoods
    .map(neighborhood => neighborhoodMap[neighborhood] || neighborhood)
    .join(', ');
};

const mask = (value) => {
  if (!value || typeof value !== 'string') return 'missing';
  if (value.length <= 6) return `${value[0]}***${value[value.length - 1]}`;
  return `${value.slice(0, 3)}***${value.slice(-3)}`;
};

const buildPlainTextBody = ({ templateParams, utmParams, recordId }) => {
  const lines = [
    'New Event Planning Request',
    `Submitted: ${templateParams.submissionTime}`,
    '',
    'Contact Information',
    `Name: ${templateParams.firstName} ${templateParams.lastName}`,
    `Email: ${templateParams.email}`,
    `Phone: ${templateParams.phone}`,
    '',
    'Event Details',
    `Occasion: ${templateParams.occasion}`,
    `Activity: ${templateParams.activity}`,
    `Guest Count: ${templateParams.guestCount}`,
    `Budget: ${templateParams.budget}`,
    `Date: ${templateParams.date}`,
    `Start Time: ${templateParams.startTime}`,
    `Duration: ${templateParams.duration}`,
    `Location: ${templateParams.location}`,
    '',
    'Food & Beverage',
    `Food: ${templateParams.foodOption}`,
    `Drink: ${templateParams.drinkOption}`,
    '',
    'Event Description',
    `${templateParams.dreamDescription}`,
    '',
    'Inspiration',
    `Link: ${templateParams.inspirationLink}`,
    `Photo: ${templateParams.inspirationPhoto}`,
    '',
    'Tracking',
    `UTM Source: ${utmParams.utm_source || utmParams.source || 'Not tracked'}`,
    `UTM Medium: ${utmParams.utm_medium || 'Not tracked'}`,
    `UTM Campaign: ${utmParams.utm_campaign || 'Not tracked'}`,
    `UTM Term: ${utmParams.utm_term || 'Not tracked'}`,
    `UTM Content: ${utmParams.utm_content || 'Not tracked'}`,
    `Referrer: ${utmParams.referrer || utmParams.ref || 'Not tracked'}`,
    '',
    `Record ID: ${recordId}`
  ];
  return lines.join('\n');
};

const emailService = {
  async submitEvent(eventDetails) {
    try {
      // Validate EmailJS configuration before proceeding
      if (!EMAILJS_SERVICE_ID || !(EMAILJS_TEMPLATE_ID || EMAILJS_TEXT_TEMPLATE_ID) || !EMAILJS_PUBLIC_KEY) {
        throw new Error('EmailJS configuration is missing. Please check your environment variables.');
      }
      
      safeLogger.info('Starting event submission via email');
      debug.log('EmailService', 'Full event details', eventDetails);

      // Emit masked config to help diagnose 400s without leaking secrets
      debug.log('EmailService', 'EmailJS config (masked)', {
        serviceId: mask(EMAILJS_SERVICE_ID),
        templateId: mask(EMAILJS_TEMPLATE_ID || ''),
        textTemplateId: mask(EMAILJS_TEXT_TEMPLATE_ID || ''),
        publicKey: mask(EMAILJS_PUBLIC_KEY)
      });
      
      // Get location data prepared for email
      const locationData = prepareLocationData(eventDetails?.setScene?.location);
      safeLogger.info('Prepared location data for email submission:', locationData);
      debug.log('EmailService', 'Location data prepared', locationData);

      // Handle budget data correctly
      let budgetValue = '';
      if (eventDetails?.setScene?.displayBudget) {
        budgetValue = eventDetails.setScene.displayBudget;
      } else if (eventDetails?.setScene?.budget) {
        budgetValue = eventDetails.setScene.budget;
        if (eventDetails.setScene.budget === 'Exact Amount' && eventDetails.setScene.customBudget) {
          budgetValue = eventDetails.setScene.customBudget;
        }
      }
      
      debug.log('EmailService', 'Prepared budget value', budgetValue);
      
      // Get UTM parameters — try dedicated storage first, fall back to form data
      let utmParams = getStoredUtmParameters();
      if (!utmParams || Object.keys(utmParams).length === 0) {
        utmParams = eventDetails._utm || {};
        debug.log('EmailService', 'UTM from dedicated storage empty, using form fallback', utmParams);
      }
      debug.log('EmailService', 'UTM parameters for submission', utmParams);

      // Format date and time for email
      const formattedDate = eventDetails.setScene?.date ? 
        dayjs(eventDetails.setScene.date).format('MMM DD, YYYY') : 'Not specified';
      
      const formattedTime = eventDetails.setScene?.startTime ? 
        dayjs(`2024-01-01T${eventDetails.setScene.startTime}`).format('h:mm A') : 'Not specified';

      // Prepare email template parameters
      const templateParams = {
        // Contact Information
        firstName: eventDetails.contact?.firstName || 'Not provided',
        lastName: eventDetails.contact?.lastName || 'Not provided',
        email: eventDetails.contact?.email || 'Not provided',
        phone: eventDetails.contact?.phone || 'Not provided',
        
        // Event Details
        occasion: eventDetails.setScene?.occasion || 'Not specified',
        activity: eventDetails.setScene?.activity || 'Not specified',
        guestCount: eventDetails.setScene?.guestCount || 'Not specified',
        budget: budgetValue || 'Not specified',
        date: formattedDate,
        startTime: formattedTime,
        duration: eventDetails.setScene?.duration || 'Not specified',
        location: locationData,
        
        // Food & Beverage
        foodOption: eventDetails?.setScene?.foodAndBev?.foodOption || 'Not specified',
        drinkOption: eventDetails?.setScene?.foodAndBev?.drinkOption || 'Not specified',
        
        // Dream Description
        dreamDescription: eventDetails.dream || 'Not provided',
        
        // Inspiration
        inspirationLink: eventDetails.inspiration?.inspirationLink || 'Not provided',
        inspirationPhoto: eventDetails.inspiration?.blobUrl ? 'Photo uploaded' : 'No photo',
        
        // Submission Details
        submissionTime: dayjs().format('MMM DD, YYYY [at] h:mm A')
      };

      // Pre-generate a recordId so it can be included in the email
      const generatedRecordId = `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // If a plain-text template is configured, send a single `message`
      if (EMAILJS_TEXT_TEMPLATE_ID) {
        const message = buildPlainTextBody({ templateParams, utmParams, recordId: generatedRecordId });
        const params = {
          message,
          reply_to: String(templateParams.email || ''),
          from_name: `${String(templateParams.firstName || '')} ${String(templateParams.lastName || '')}`.trim(),
          recordId: generatedRecordId,
          submissionTime: templateParams.submissionTime
        };

        const response = await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEXT_TEMPLATE_ID,
          params,
          { publicKey: EMAILJS_PUBLIC_KEY }
        );

        debug.log('EmailService', 'Plain-text email sent successfully', response);
        safeLogger.success('Email submission successful (plain-text)', response);
        return { success: true, recordId: generatedRecordId, message: 'Email sent successfully' };
      }

      // Otherwise use the rich template with safe string parameters
      const compiled = buildPlainTextBody({ templateParams, utmParams, recordId: generatedRecordId });
      const aliasParams = {
        from_name: `${(templateParams.firstName || '').toString()} ${(templateParams.lastName || '').toString()}`.trim(),
        reply_to: (templateParams.email || '').toString(),
        to_name: 'LetsHost Team',
        message: compiled,
        recordId: generatedRecordId,
        utmSource: utmParams.utm_source || utmParams.source || 'Not tracked',
        utmMedium: utmParams.utm_medium || 'Not tracked',
        utmCampaign: utmParams.utm_campaign || 'Not tracked',
        utmTerm: utmParams.utm_term || 'Not tracked',
        utmContent: utmParams.utm_content || 'Not tracked',
        utmReferrer: utmParams.referrer || utmParams.ref || 'Not tracked'
      };

      const safeParams = Object.fromEntries(
        Object.entries({ ...templateParams, ...aliasParams }).map(([key, value]) => [
          key,
          value === null || value === undefined ? '' : String(value)
        ])
      );

      debug.log('EmailService', 'Template parameters prepared', safeParams);

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        safeParams,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );

      debug.log('EmailService', 'Email sent successfully', response);
      safeLogger.success('Email submission successful', response);
      
      return {
        success: true,
        recordId: generatedRecordId,
        message: 'Email sent successfully'
      };
    } catch (error) {
      debug.error('EmailService', 'Email submission error details', error);
      const status = error?.status || 'unknown';
      const text = error?.text || error?.message || 'Unknown error';
      safeLogger.error('Email submission error', { status, text });
      const enriched = new Error(typeof text === 'string' ? text : 'Email submission failed');
      enriched.status = status;
      throw enriched;
    }
  },

  // Mock function to maintain compatibility with existing payment flow
  async updatePaymentStatus(recordId) {
    try {
      safeLogger.info('Updating payment status for email record', { recordId });
      
      // Since we're using email, we'll just log this for now
      // In a real implementation, you might want to send a follow-up email
      // or update a database record
      safeLogger.success('Payment status updated for email record', { recordId });
      
      return {
        success: true,
        recordId: recordId,
        paymentStatus: 'Paid'
      };
    } catch (error) {
      safeLogger.error('Error updating payment status for email record', error);
      throw error;
    }
  }
};

export default emailService; 